// ============================================================
// Q2 Step Editors
// Custom slide-out panel bodies for specific Q2 SEO project steps.
//
// Used by SEOWorkspace.js → ProjectItemSidePanel — when the open
// item matches a Q2 step type, render one of these editors instead
// of the generic panel body.
//
// Detection helper:
//   import { getQ2StepType, ... } from './Q2StepEditors'
//   const stepType = getQ2StepType(item, project)
//   if (stepType) {
//     // render <Q2StepEditor stepType={stepType} ... />
//   }
// ============================================================
import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { Plus, X, Trash2, ExternalLink, Send, CheckCircle, Download } from 'lucide-react'
import { AEOGridEditor, AEO_REFERENCE_URL } from './AEOGridTab'
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx'
import { isActiveProfile } from './constants'

// Q2 template IDs (must match migration 23)
const TPL_AEO  = 'tpl-old-blogs-aeo-q2-2026'
const TPL_RANK = 'tpl-rank-tracker-q2-2026'

// Reference docs shared across Q2 work
const BLOG_STRUCTURE_DOC_URL = 'https://docs.google.com/document/d/1-qgF1l_0k46fkTLWUnjLUplTn4IKTM1x1ZxtwSwi39U/edit?usp=sharing'
const RANK_KEYWORDS_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1qbkOcPq10_oMYVTfuCgh3U-cJXUG3GyU57F4KOFG8j8/edit?usp=sharing'

// ─── Detect what kind of Q2 step this is (if any) ────────────
// Returns one of: 'select_blogs' | 'focus_keywords' | 'pages_step' | 'blog_child' | 'aeo_grid' | 'research_guidance' | null
export function getQ2StepType(item, project) {
  if (!item || !project) return null
  const topic = (item.topic || '').trim()

  if (project.template_id === TPL_AEO) {
    if (topic === 'Create AEO Grid') return 'aeo_grid'
    if (topic === 'Select Blogs') return 'select_blogs'
    if (topic === 'AEO Research Guidance') return 'research_guidance'
    if (topic.startsWith('Blog: ') || /^Blog\s*\d+$/.test(topic)) return 'blog_child'
  }

  if (project.template_id === TPL_RANK) {
    if (topic === 'Analyze the Rankings') return 'analyze_rankings'
    if (topic === 'Select 5 Focus Keywords') return 'focus_keywords'
    if (topic === 'Pages') return 'pages_step'
  }

  return null
}

// ─── Shared: internal approval flow (assignee → Lauren → complete) ───────────
// Steps that route to the assigner (Lauren). Uses the existing
// awaiting_ad_approval + approval_ad columns. approval_ad === complete.
function StepApproval({ item, onUpdated, optimisticReady }) {
  const toast = useToast()
  const awaitingAd = item.awaiting_ad_approval && !item.approval_ad
  const approved = !!item.approval_ad

  const update = async (patch, msg) => {
    const { error } = await supabase.from('seo_project_items').update(patch).eq('id', item.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    if (msg) toast(msg, 'success')
    onUpdated && onUpdated()
  }

  const sendForApproval = () => update({ awaiting_ad_approval: true }, 'Sent to Lauren for approval')
  const approve = () => update({ approval_ad: true, awaiting_ad_approval: false, status: 'complete' }, 'Approved — step complete')
  const reopen = () => update({ approval_ad: false, awaiting_ad_approval: false, status: 'in_progress' }, 'Reopened')

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
        Approval
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8,
        background: approved ? '#e8f5eb' : 'var(--cream)',
        border: `1px solid ${approved ? '#c2dec8' : 'var(--border-light)'}`,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          background: approved ? '#2e6e3a' : '#fff', border: `1.5px solid ${approved ? '#2e6e3a' : 'var(--border)'}`,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
        }}>{approved ? '✓' : ''}</div>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: approved ? '#2e6e3a' : 'var(--text-primary)' }}>
          {approved ? '✓ Approved by Lauren — complete' : awaitingAd ? 'Awaiting Lauren\u2019s approval' : 'Lauren\u2019s approval (internal)'}
        </div>
        {!approved && !awaitingAd && (
          <button className="btn btn-gold btn-sm" disabled={!optimisticReady} onClick={sendForApproval} style={{ flexShrink: 0 }}>
            <Send size={12} /> Send to Lauren
          </button>
        )}
        {!approved && awaitingAd && (
          <button className="btn btn-gold btn-sm" onClick={approve} style={{ flexShrink: 0 }}>
            <CheckCircle size={12} /> Approve
          </button>
        )}
      </div>
      {!optimisticReady && !approved && !awaitingAd && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Save the step\u2019s work first, then send for approval.</div>
      )}
      {(approved || awaitingAd) && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={reopen}>↺ Reopen / unlock</button>
      )}
    </div>
  )
}


export function Q2StepEditor({ stepType, item, project, client, profiles, currentUser, onUpdated }) {
  switch (stepType) {
    case 'aeo_grid':
      return <AEOGridStepEditor item={item} client={client} project={project} profiles={profiles} currentUser={currentUser} onUpdated={onUpdated} />
    case 'select_blogs':
      return <SelectBlogsEditor item={item} project={project} onUpdated={onUpdated} />
    case 'research_guidance':
      return <ResearchGuidanceEditor item={item} project={project} client={client} profiles={profiles} currentUser={currentUser} onUpdated={onUpdated} />
    case 'analyze_rankings':
      return <AnalyzeRankingsEditor item={item} onUpdated={onUpdated} />
    case 'focus_keywords':
      return <FocusKeywordsEditor item={item} onUpdated={onUpdated} />
    case 'pages_step':
      return <PagesStepEditor item={item} project={project} client={client} profiles={profiles} onUpdated={onUpdated} />
    case 'blog_child':
      return <BlogChildEditor item={item} onUpdated={onUpdated} />
    default:
      return null
  }
}

// ============================================================
// 0. AEO Grid step (Old Blogs - AEO step 1)
//    Shows the client's existing grids + a (+) button that opens
//    the full AEOGridEditor popup. Grids are stored on the client
//    (aeo_grids table) so they also appear in the AEO Grids tab.
// ============================================================
function AEOGridStepEditor({ item, client, project, profiles, currentUser, onUpdated }) {
  const toast = useToast()
  const [grids, setGrids] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingGrid, setEditingGrid] = useState(null) // grid object or 'new'

  const loadGrids = async () => {
    if (!client?.id) { setLoading(false); return }
    const { data, error } = await supabase
      .from('aeo_grids')
      .select('*')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false })
    if (error) { toast('Error loading grids: ' + error.message, 'error') }
    setGrids(data || [])
    setLoading(false)
  }

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!client?.id) { setLoading(false); return }
      const { data } = await supabase
        .from('aeo_grids')
        .select('*')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false })
      if (!cancelled) { setGrids(data || []); setLoading(false) }
    }
    run()
    return () => { cancelled = true }
  }, [client?.id])

  const statusLabel = s =>
    s === 'approved' ? '✓ Approved' : s === 'awaiting_approval' ? '⏳ Awaiting approval' : 'Draft'

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Build the client's AEO content grid here, then send it to Lauren for approval.
        Grids are saved to the client's <strong>AEO Grids</strong> tab too.
        <a href={AEO_REFERENCE_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', marginLeft: 6 }}>See example ↗</a>
      </div>

      <button className="btn btn-gold" style={{ width: '100%' }} onClick={() => setEditingGrid('new')}>
        <Plus size={14} /> Create AEO Grid
      </button>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
      ) : grids.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
            Grids for this client ({grids.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {grids.map(g => {
              const personaCount = Array.isArray(g.personas) ? g.personas.length : 0
              return (
                <div key={g.id} onClick={() => setEditingGrid(g)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', background: 'var(--cream)', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${g.status === 'approved' ? '#c2dec8' : 'var(--border-light)'}`,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{personaCount} {personaCount === 1 ? 'persona' : 'personas'}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: g.status === 'approved' ? '#2e6e3a' : g.status === 'awaiting_approval' ? '#8a6000' : 'var(--text-muted)' }}>
                    {statusLabel(g.status)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {editingGrid && (
        <AEOGridEditor
          client={client}
          profiles={profiles}
          currentUser={currentUser}
          grid={editingGrid === 'new' ? null : editingGrid}
          onClose={() => setEditingGrid(null)}
          onSaved={() => { setEditingGrid(null); loadGrids(); onUpdated && onUpdated() }}
        />
      )}

      <StepApproval item={item} onUpdated={onUpdated} optimisticReady={grids.length > 0} />
    </div>
  )
}

// ============================================================
// 1. Select Blogs editor (Old Blogs - AEO step 2)
//    5 rows: blog title + reasoning. On save, also creates
//    "Blog: [title]" child items in the same project.
// ============================================================
function SelectBlogsEditor({ item, project, onUpdated }) {
  const toast = useToast()
  const initialRows = Array.isArray(item.list_items) && item.list_items.length > 0
    ? item.list_items
    : [
        { title: '', reasoning: '' },
        { title: '', reasoning: '' },
        { title: '', reasoning: '' },
        { title: '', reasoning: '' },
        { title: '', reasoning: '' },
      ]
  const [rows, setRows] = useState(initialRows)
  const [saving, setSaving] = useState(false)

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  const save = async () => {
    setSaving(true)
    try {
      const filled = rows.filter(r => r.title && r.title.trim())

      // Status reflects progress; the approval flow is what marks it complete.
      // Don't override an already-approved (complete) step on a re-save.
      let newStatus = item.status === 'complete' ? 'complete' : (filled.length > 0 ? 'in_progress' : 'not_started')

      // 1. Save the list onto the Select Blogs item itself
      const { error: itemErr } = await supabase
        .from('seo_project_items')
        .update({ list_items: filled, status: newStatus })
        .eq('id', item.id)
      if (itemErr) throw itemErr

      // 2. Get the project's existing Blog child items
      const { data: existing } = await supabase
        .from('seo_project_items')
        .select('id, topic, sort_order')
        .eq('project_id', project.id)
        .like('topic', 'Blog: %')
        .order('sort_order')

      const existingChildren = existing || []

      // 3. Reconcile: upsert by index. Update existing in-place, insert new.
      //    Delete leftover children if the user shortened the list.
      const baseSort = 100 // child items sit after the 3 parent steps (which use 10,20,30)
      for (let i = 0; i < filled.length; i++) {
        const childTopic = `Blog: ${filled[i].title.trim()}`
        if (existingChildren[i]) {
          // Update existing
          await supabase
            .from('seo_project_items')
            .update({ topic: childTopic, sort_order: baseSort + i * 10 })
            .eq('id', existingChildren[i].id)
        } else {
          // Insert new child
          await supabase.from('seo_project_items').insert({
            project_id: project.id,
            topic: childTopic,
            notes: filled[i].reasoning || null,
            sort_order: baseSort + i * 10,
            status: 'not_started',
            page_or_blog: 'blog',
          })
        }
      }

      // Delete extras (user removed some blogs)
      const extras = existingChildren.slice(filled.length)
      if (extras.length > 0) {
        await supabase
          .from('seo_project_items')
          .delete()
          .in('id', extras.map(c => c.id))
      }

      toast(`Saved — ${filled.length} blog ${filled.length === 1 ? 'item' : 'items'} ready in the project`, 'success')
      onUpdated && onUpdated()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Pick 5 blogs that align with the client's primary focus keywords but aren't ranking well yet.
        They should fit neatly into the AEO grid somewhere. When you save, each blog will appear as
        its own item in this project so the technician can pick them up.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 1.4fr', gap: 8, alignItems: 'flex-start' }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12, background: 'var(--navy)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, marginTop: 4,
            }}>{idx + 1}</div>
            <input
              className="form-control"
              value={r.title || ''}
              onChange={e => updateRow(idx, 'title', e.target.value)}
              placeholder="Blog title or H1"
              style={{ fontSize: 13 }}
            />
            <input
              className="form-control"
              value={r.reasoning || ''}
              onChange={e => updateRow(idx, 'reasoning', e.target.value)}
              placeholder="Reasoning (target keyword, why this blog, etc.)"
              style={{ fontSize: 13 }}
            />
          </div>
        ))}
      </div>

      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setRows(rs => [...rs, { title: '', reasoning: '' }])}
        style={{ marginTop: 10 }}
      >
        <Plus size={12} /> Add another blog
      </button>

      <div style={{ marginTop: 18 }}>
        <button className="btn btn-gold" style={{ width: '100%' }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save blog list & create work items'}
        </button>
      </div>

      <StepApproval item={item} onUpdated={onUpdated} optimisticReady={rows.filter(r => r.title && r.title.trim()).length >= 5} />
    </div>
  )
}

// ============================================================
// 2. Focus Keywords editor (Rank Tracker step 1)
//    5 rows: keyword + reasoning. Saves to list_items.
//    No child items spawn — keywords are tracked in AHREF.
// ============================================================
function FocusKeywordsEditor({ item, onUpdated }) {
  const toast = useToast()
  const initialRows = Array.isArray(item.list_items) && item.list_items.length > 0
    ? item.list_items
    : [
        { keyword: '', reasoning: '' },
        { keyword: '', reasoning: '' },
        { keyword: '', reasoning: '' },
        { keyword: '', reasoning: '' },
        { keyword: '', reasoning: '' },
      ]
  const [rows, setRows] = useState(initialRows)
  const [saving, setSaving] = useState(false)

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  const save = async () => {
    setSaving(true)
    const filled = rows.filter(r => r.keyword && r.keyword.trim())
    // Status reflects progress; the approval flow marks it complete.
    let newStatus = item.status === 'complete' ? 'complete' : (filled.length > 0 ? 'in_progress' : 'not_started')
    const { error } = await supabase
      .from('seo_project_items')
      .update({ list_items: filled, status: newStatus })
      .eq('id', item.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(`Saved ${filled.length} focus ${filled.length === 1 ? 'keyword' : 'keywords'}`, 'success')
    onUpdated && onUpdated()
  }

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Select ~5 primary focus keywords. Reference recent client answers and changes.
        Insert these into the rank tracker in the client's AHREF project.
      </div>

      {/* Reference: the keywords/topics sheet the clients are trying to rank for */}
      <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf6e3', border: '1px solid #f0d98a', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>📊</span>
        <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          This sheet has the keywords/topics your clients are trying to rank for — start here.
        </div>
        <a href={RANK_KEYWORDS_SHEET_URL} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}>
          <ExternalLink size={12} /> Keywords sheet
        </a>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 1.4fr', gap: 8, alignItems: 'flex-start' }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12, background: 'var(--navy)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, marginTop: 4,
            }}>{idx + 1}</div>
            <input
              className="form-control"
              value={r.keyword || ''}
              onChange={e => updateRow(idx, 'keyword', e.target.value)}
              placeholder="Keyword phrase"
              style={{ fontSize: 13 }}
            />
            <input
              className="form-control"
              value={r.reasoning || ''}
              onChange={e => updateRow(idx, 'reasoning', e.target.value)}
              placeholder="Why this keyword? (search volume, intent, etc.)"
              style={{ fontSize: 13 }}
            />
          </div>
        ))}
      </div>

      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setRows(rs => [...rs, { keyword: '', reasoning: '' }])}
        style={{ marginTop: 10 }}
      >
        <Plus size={12} /> Add another keyword
      </button>

      <div style={{ marginTop: 18 }}>
        <button className="btn btn-gold" style={{ width: '100%' }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save keyword list'}
        </button>
      </div>

      <StepApproval item={item} onUpdated={onUpdated} optimisticReady={rows.filter(r => r.keyword && r.keyword.trim()).length >= 5} />
    </div>
  )
}

// ============================================================
// 3. Pages step editor (Rank Tracker step 3)
//    Each row creates a row in seo_pages (the Yoast Pages tab).
//    - Overall guidance notes saved to the step item's `notes`
//    - Per-page notes saved to each seo_pages row's `notes`
//    - No auto-complete; manual Mark complete + Reopen
//    - Delete pages that were added from this project
// ============================================================
function PagesStepEditor({ item, project, client, profiles, onUpdated }) {
  const toast = useToast()
  const [existingPages, setExistingPages] = useState([])
  const [guidance, setGuidance] = useState(item.notes || '')
  const blankRow = () => ({ page_name: '', page_url: '', keyword_choice: '', assigned_to: '', due_date: '', notes: '' })
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow()])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  const PROJECT_TAG = `From rank tracker project: ${project.name}`

  const fetchPages = async () => {
    const { data } = await supabase
      .from('seo_pages')
      .select('id, page_name, page_url, keyword_choice, assigned_to, yoast_rating, notes')
      .eq('client_id', project.client_id)
      .order('created_at', { ascending: false })
    return data || []
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const data = await fetchPages()
      if (!cancelled) { setExistingPages(data); setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [project.client_id])

  const updateRow = (idx, field, value) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }
  const addRow = () => setRows(rs => [...rs, blankRow()])
  const removeRow = idx => setRows(rs => rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs)

  const saveGuidance = async () => {
    const { error } = await supabase.from('seo_project_items').update({ notes: guidance || null }).eq('id', item.id)
    if (error) { toast('Error saving notes: ' + error.message, 'error'); return }
    toast('Guidance saved', 'success')
    onUpdated && onUpdated()
  }

  const addPages = async () => {
    const filled = rows.filter(r => r.page_name && r.page_name.trim())
    if (filled.length === 0) { toast('Add at least one page', 'error'); return }
    setSaving(true)
    try {
      const payload = filled.map(r => ({
        client_id: project.client_id,
        page_name: r.page_name.trim(),
        page_url: r.page_url?.trim() || null,
        keyword_choice: r.keyword_choice?.trim() || null,
        assigned_to: r.assigned_to || null,
        due_date: r.due_date || null,
        // per-page notes; tag the source project so we know it can be undone here
        notes: r.notes?.trim() ? `${r.notes.trim()}\n(${PROJECT_TAG})` : PROJECT_TAG,
      }))
      const { error } = await supabase.from('seo_pages').insert(payload)
      if (error) throw error

      // also persist the overall guidance if they typed any
      if (guidance !== (item.notes || '')) {
        await supabase.from('seo_project_items').update({ notes: guidance || null }).eq('id', item.id)
      }

      toast(`Added ${filled.length} ${filled.length === 1 ? 'page' : 'pages'} to Yoast Pages tab`, 'success')
      setExistingPages(await fetchPages())
      setRows([blankRow(), blankRow(), blankRow()])
      onUpdated && onUpdated()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const deletePage = async (page) => {
    if (!window.confirm(`Delete page "${page.page_name}" from the Yoast Pages tab?`)) return
    const { error } = await supabase.from('seo_pages').delete().eq('id', page.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Page deleted', 'success')
    setExistingPages(await fetchPages())
    onUpdated && onUpdated()
  }

  const isComplete = item.status === 'complete'
  const setComplete = async (complete) => {
    const { error } = await supabase
      .from('seo_project_items')
      .update({ status: complete ? 'complete' : 'in_progress' })
      .eq('id', item.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(complete ? 'Step marked complete' : 'Step reopened', 'success')
    onUpdated && onUpdated()
  }

  // Client sign-off (uses approval_client). Checking it also completes the step.
  const clientSignedOff = !!item.approval_client
  const toggleClientSignoff = async (checked) => {
    const { error } = await supabase
      .from('seo_project_items')
      .update({ approval_client: checked, status: checked ? 'complete' : 'in_progress' })
      .eq('id', item.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(checked ? 'Client sign-off recorded — step complete' : 'Sign-off removed', 'success')
    onUpdated && onUpdated()
  }
  const emailClientForApproval = () => {
    const to = client?.contact_email || ''
    const subject = encodeURIComponent(`Page updates for approval — ${client?.name || ''}`)
    const body = encodeURIComponent(`Hi,\n\nWe'd like your approval on the page updates we're planning for your site. Please reply to confirm these look good.\n\nBest,\nLMR Team`)
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(to)}&su=${subject}&body=${body}`, '_blank')
    toast('Opened client approval email', 'success')
  }

  // Pages that came from THIS project (deletable/undoable here)
  const fromThisProject = p => (p.notes || '').includes(PROJECT_TAG)

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Which keywords do we need to focus on vs. which are we already ranking well for? For the ones that need help,
        select the right web pages to update. Pages you add here become rows in this client's <strong>Yoast Pages</strong> tab,
        ready for the optimization workflow (alt text, meta, slug, GSC submit, etc.).
        Use a combination of <strong>AEO + SEO</strong> when writing the brief — add FAQs and statistics, and research what
        pages already ranking for this keyword include on-page and in their metadata.
      </div>

      {/* Overall guidance notes */}
      <div className="form-group">
        <label className="form-label">Brief / guidance for the assignee (overall)</label>
        <textarea
          className="form-control"
          rows={3}
          value={guidance}
          onChange={e => setGuidance(e.target.value)}
          placeholder="What should the assignee change or add across these pages? AEO+SEO direction, FAQ/stat ideas, competitor research notes…"
        />
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={saveGuidance}>💾 Save guidance</button>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, marginTop: 8 }}>
        New pages to add
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((r, idx) => (
          <div key={idx} style={{ border: '1px solid var(--border-light)', borderRadius: 8, padding: 10, background: 'var(--cream)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 130px 28px', gap: 6, alignItems: 'center' }}>
              <input className="form-control" value={r.page_name} onChange={e => updateRow(idx, 'page_name', e.target.value)} placeholder="Page name *" style={{ fontSize: 12.5, padding: '5px 8px' }} />
              <input className="form-control" value={r.page_url} onChange={e => updateRow(idx, 'page_url', e.target.value)} placeholder="URL" style={{ fontSize: 12.5, padding: '5px 8px' }} />
              <input className="form-control" value={r.keyword_choice} onChange={e => updateRow(idx, 'keyword_choice', e.target.value)} placeholder="Target keyword" style={{ fontSize: 12.5, padding: '5px 8px' }} />
              <select className="form-control" value={r.assigned_to} onChange={e => updateRow(idx, 'assigned_to', e.target.value)} style={{ fontSize: 12.5, padding: '5px 8px' }}>
                <option value="">— Assign to —</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
              <input className="form-control" type="date" value={r.due_date} onChange={e => updateRow(idx, 'due_date', e.target.value)} title="Due date" style={{ fontSize: 12.5, padding: '5px 8px' }} />
              <button className="btn-icon" onClick={() => removeRow(idx)} title="Remove row"><X size={12} /></button>
            </div>
            <textarea
              className="form-control"
              rows={2}
              value={r.notes}
              onChange={e => updateRow(idx, 'notes', e.target.value)}
              placeholder="Notes for this page — what to change/add (FAQs, stats, sections, internal links)…"
              style={{ fontSize: 12.5, marginTop: 6 }}
            />
          </div>
        ))}
      </div>

      <button className="btn btn-ghost btn-sm" onClick={addRow} style={{ marginTop: 10 }}>
        <Plus size={12} /> Add another page
      </button>

      <div style={{ marginTop: 14 }}>
        <button className="btn btn-gold" style={{ width: '100%' }} onClick={addPages} disabled={saving}>
          {saving ? 'Saving…' : '➕ Add pages to Yoast Pages tab'}
        </button>
      </div>

      {/* Client sign-off */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
          Client sign-off
        </div>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
          padding: '10px 12px', borderRadius: 8,
          background: clientSignedOff ? '#e8f5eb' : 'var(--cream)',
          border: `1px solid ${clientSignedOff ? '#c2dec8' : 'var(--border-light)'}`,
        }}>
          <input type="checkbox" checked={clientSignedOff} onChange={e => toggleClientSignoff(e.target.checked)} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: clientSignedOff ? '#2e6e3a' : 'var(--text-primary)' }}>
              {clientSignedOff ? '✓ Client signed off — step complete' : 'Client signed off on these changes'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Check this once the client has approved the page updates.
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={e => { e.preventDefault(); emailClientForApproval() }} style={{ flexShrink: 0 }}>
            📨 Email for approval
          </button>
        </label>

        {/* Manual complete fallback */}
        <div style={{ marginTop: 10 }}>
          {!isComplete ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setComplete(true)}>
              <CheckCircle size={12} /> Mark complete without client sign-off
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={() => setComplete(false)}>
              ↺ Reopen this step
            </button>
          )}
        </div>
      </div>

      {/* Existing pages */}
      {!loading && existingPages.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
            Already in Yoast Pages tab ({existingPages.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {existingPages.map(p => {
              const assignee = profiles.find(pf => pf.id === p.assigned_to)
              const deletable = fromThisProject(p)
              return (
                <div key={p.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 10px', background: 'var(--cream)', borderRadius: 6, fontSize: 12,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{p.page_name}</div>
                    {p.keyword_choice && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>🔑 {p.keyword_choice}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {assignee && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{assignee.full_name}</span>}
                    {p.page_url && (
                      <a href={p.page_url} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>
                        <ExternalLink size={11} />
                      </a>
                    )}
                    {deletable && (
                      <button className="btn-icon" style={{ color: 'var(--red)' }} title="Delete this page" onClick={() => deletePage(p)}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 6 }}>
            Only pages added from this project show a delete button here. Others are managed in the Yoast Pages tab.
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Analyze the Rankings step (Rank Tracker step 1)
//   - AHREF rank-tracker screenshot LINK + analysis notes
//   - Stored in the step item's `brief` jsonb
// ============================================================
function AnalyzeRankingsEditor({ item, onUpdated }) {
  const toast = useToast()
  const b = (item.brief && typeof item.brief === 'object') ? item.brief : {}
  const [screenshotUrl, setScreenshotUrl] = useState(b.screenshot_url || '')
  const [analysisNotes, setAnalysisNotes] = useState(b.analysis_notes || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const url = screenshotUrl.trim()
    const notes = analysisNotes.trim()
    const brief = { screenshot_url: url, analysis_notes: notes }
    // Status reflects progress; the approval flow marks it complete.
    let newStatus = item.status === 'complete' ? 'complete' : (url || notes ? 'in_progress' : 'not_started')
    const { error } = await supabase.from('seo_project_items').update({ brief, status: newStatus }).eq('id', item.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Saved', 'success')
    onUpdated && onUpdated()
  }

  const isImageLink = /\.(png|jpe?g|gif|webp)(\?|$)/i.test(screenshotUrl)

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Which keywords do we need to focus on vs. which are we already ranking well for? For the ones that need help,
        pick the web pages we can update. Use a combination of <strong>AEO + SEO</strong> when writing the brief to improve the page.
      </div>

      <div className="form-group">
        <label className="form-label">AHREF rank-tracker screenshot (link)</label>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
          Take a screenshot of the AHREF rank-tracker tab, drop it in Drive/Dropbox, and paste the share link here so the team can see whether we're ranking for any of the focus keywords yet.
        </div>
        <input
          className="form-control"
          value={screenshotUrl}
          onChange={e => setScreenshotUrl(e.target.value)}
          placeholder="https://drive.google.com/… or an image URL"
        />
        {screenshotUrl && (
          <div style={{ marginTop: 8 }}>
            {isImageLink ? (
              <a href={screenshotUrl} target="_blank" rel="noreferrer">
                <img src={screenshotUrl} alt="AHREF rank tracker" style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border-light)' }} />
              </a>
            ) : (
              <a href={screenshotUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                <ExternalLink size={12} /> Open screenshot link
              </a>
            )}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Analysis notes — which pages will you rework?</label>
        <textarea
          className="form-control"
          rows={5}
          value={analysisNotes}
          onChange={e => setAnalysisNotes(e.target.value)}
          placeholder="Brief notes on the rankings and which pages you plan to rework (and why)…"
        />
      </div>

      <div style={{ marginTop: 4 }}>
        <button className="btn btn-gold" style={{ width: '100%' }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save analysis'}
        </button>
      </div>

      <StepApproval item={item} onUpdated={onUpdated} optimisticReady={!!screenshotUrl.trim() && !!analysisNotes.trim()} />
    </div>
  )
}

// ============================================================
// 4. Blog child editor (auto-created Blog: [Title] items)
//    Final URL + Brief link fields.
// ============================================================
function BlogChildEditor({ item, onUpdated }) {
  const toast = useToast()
  const [finalUrl, setFinalUrl] = useState(item.final_url || '')
  const [briefLink, setBriefLink] = useState(item.original_url || '')
  const [saving, setSaving] = useState(false)
  const [approved, setApproved] = useState(!!item.approval_ad)

  const save = async () => {
    setSaving(true)
    const { error } = await supabase
      .from('seo_project_items')
      .update({
        final_url: finalUrl.trim() || null,
        original_url: briefLink.trim() || null,
      })
      .eq('id', item.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Blog deliverables saved', 'success')
    onUpdated && onUpdated()
  }

  // The "Approved" checkbox both records approval and completes the step.
  const toggleApproved = async (checked) => {
    setApproved(checked)
    const { error } = await supabase
      .from('seo_project_items')
      .update({ approval_ad: checked, status: checked ? 'complete' : 'in_progress' })
      .eq('id', item.id)
    if (error) { toast('Error: ' + error.message, 'error'); setApproved(!checked); return }
    toast(checked ? 'Blog approved & marked complete' : 'Approval removed', 'success')
    onUpdated && onUpdated()
  }

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        The brief tells the technician what to write. The final URL is the live blog after publish.
      </div>

      {/* Brief content (written in AEO Research Guidance, stored on this blog) */}
      {(() => {
        const br = (item.brief && typeof item.brief === 'object') ? item.brief : null
        const hasBrief = br && (br.h1_question || br.author || br.tldr || (br.h2_questions || []).length || (br.faqs || []).length || br.company_blurb || br.notes_quotes_stats)
        if (!hasBrief) return null
        const Row = ({ label, children }) => (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.55 }}>{children}</div>
          </div>
        )
        return (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gold)', marginBottom: 10 }}>
              📋 Blog Brief
            </div>
            <div style={{ background: '#fff', border: '1px solid var(--border-light)', borderRadius: 10, padding: '16px 18px' }}>
              {br.h1_question && <Row label="H1 (as a question)"><strong>{br.h1_question}</strong></Row>}
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {br.date_updated && <Row label="Last updated (in post)">{br.date_updated}</Row>}
                {br.author && <Row label="Author">{br.author}</Row>}
              </div>
              {br.author_bio && <Row label="Author bio">{br.author_bio}</Row>}
              <Row label="TL;DR">{br.tldr_default ? <em style={{ color: 'var(--text-muted)' }}>Technician to write</em> : br.tldr}</Row>

              {(br.h2_questions || []).length > 0 && (
                <Row label="H2 sections (as questions)">
                  <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {br.h2_questions.map((h, i) => <li key={i} style={{ marginBottom: 4 }}>{h}</li>)}
                  </ol>
                </Row>
              )}

              {(br.faqs || []).length > 0 && (
                <Row label="FAQ section">
                  <div style={{ marginTop: 4 }}>
                    {br.faqs.map((f, i) => (
                      <div key={i} style={{ marginBottom: 10, paddingLeft: 12, borderLeft: '3px solid var(--cream-dark)' }}>
                        <div style={{ fontWeight: 600 }}>Q: {f.q}</div>
                        <div style={{ color: 'var(--text-secondary)' }}>A: {f.a}</div>
                      </div>
                    ))}
                  </div>
                </Row>
              )}

              {br.company_blurb && <Row label="About the company">{br.company_blurb}</Row>}
              {br.notes_quotes_stats && (
                <Row label="Notes, quotes & statistics">
                  <div style={{ whiteSpace: 'pre-wrap' }}>{br.notes_quotes_stats}</div>
                </Row>
              )}
            </div>
          </div>
        )
      })()}

      {/* Reference: the new blog structure they're aiming for */}
      <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf6e3', border: '1px solid #f0d98a', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>📄</span>
        <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          Use the brief above to achieve the structure shown in this reference doc.
        </div>
        <a href={BLOG_STRUCTURE_DOC_URL} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}>
          <ExternalLink size={12} /> New structure doc
        </a>
      </div>

      <div className="form-group">
        <label className="form-label">Brief Link</label>
        <input
          className="form-control"
          value={briefLink}
          onChange={e => setBriefLink(e.target.value)}
          placeholder="https://docs.google.com/... (the brief doc for the technician)"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Final URL (after publish)</label>
        <input
          className="form-control"
          value={finalUrl}
          onChange={e => setFinalUrl(e.target.value)}
          placeholder="https://... (the live blog URL)"
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="btn btn-gold" style={{ width: '100%' }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save'}
        </button>
      </div>

      {/* Approval — send the first few to Lauren (Slack), then check this off */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
          padding: '10px 12px', borderRadius: 8,
          background: approved ? '#e8f5eb' : 'var(--cream)',
          border: `1px solid ${approved ? '#c2dec8' : 'var(--border-light)'}`,
        }}>
          <input type="checkbox" checked={approved} onChange={e => toggleApproved(e.target.checked)} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: approved ? '#2e6e3a' : 'var(--text-primary)' }}>
              {approved ? '✓ Approved — step complete' : 'Approved'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Send the first few to Lauren in Slack to confirm they're on track. Once approved, check this to complete the step.
            </div>
          </div>
        </label>
      </div>
    </div>
  )
}

// ============================================================
// Brief delivery helpers — plain-text (email body) + .docx
// ============================================================
const stripBlogPrefix = t => (t || '').replace(/^Blog:\s*/, '')

// One blog's brief as a plain-text block (for email body)
function briefToText(blog, idx) {
  const b = (blog.brief && typeof blog.brief === 'object') ? blog.brief : {}
  const lines = []
  lines.push(`${idx + 1}. ${stripBlogPrefix(blog.topic) || '(untitled blog)'}`)
  if (b.h1_question)  lines.push(`   H1: ${b.h1_question}`)
  if (b.date_updated) lines.push(`   Last Updated (in post): ${b.date_updated}`)
  if (b.author)       lines.push(`   Author: ${b.author}`)
  if (b.author_bio)   lines.push(`   Author bio: ${b.author_bio}`)
  if (b.tldr_default) lines.push(`   TL;DR: (technician to write)`)
  else if (b.tldr)    lines.push(`   TL;DR: ${b.tldr}`)
  if (Array.isArray(b.h2_questions) && b.h2_questions.length) {
    lines.push(`   H2 sections:`)
    b.h2_questions.forEach(h => lines.push(`     - ${h}`))
  }
  if (Array.isArray(b.faqs) && b.faqs.length) {
    lines.push(`   FAQs:`)
    b.faqs.forEach(f => { lines.push(`     Q: ${f.q}`); lines.push(`     A: ${f.a}`) })
  }
  if (b.company_blurb)     lines.push(`   About the company: ${b.company_blurb}`)
  if (b.notes_quotes_stats) lines.push(`   Notes/Quotes/Stats: ${b.notes_quotes_stats}`)
  return lines.join('\n')
}

// All briefs as one plain-text body
function allBriefsToText(blogs, clientName) {
  const header = `Blog briefs for ${clientName || 'your practice'} (${blogs.length}):\n`
  return header + '\n' + blogs.map((blog, i) => briefToText(blog, i)).join('\n\n')
}

// Build a combined .docx of all briefs (uses the same docx API as MeetingNotes.js)
async function downloadBriefsDocx(blogs, clientName) {
  const children = []

  children.push(new Paragraph({
    children: [new TextRun({ text: `AEO Blog Briefs — ${clientName || ''}`, bold: true, size: 36, color: '1a2b4a' })],
    spacing: { after: 100 },
  }))
  children.push(new Paragraph({
    children: [new TextRun({ text: `${blogs.length} blog${blogs.length === 1 ? '' : 's'} · Generated ${new Date().toLocaleDateString()}`, size: 22, color: '666666' })],
    spacing: { after: 300 },
  }))

  blogs.forEach((blog, idx) => {
    const b = (blog.brief && typeof blog.brief === 'object') ? blog.brief : {}

    children.push(new Paragraph({
      text: `${idx + 1}. ${stripBlogPrefix(blog.topic) || '(untitled blog)'}`,
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 280, after: 120 },
    }))

    const field = (label, value) => {
      if (!value) return
      children.push(new Paragraph({
        children: [
          new TextRun({ text: `${label}: `, bold: true, size: 22 }),
          new TextRun({ text: String(value), size: 22 }),
        ],
        spacing: { after: 80 },
      }))
    }

    field('H1', b.h1_question)
    field('Last Updated (in post)', b.date_updated)
    field('Author', b.author)
    field('Author bio', b.author_bio)
    field('TL;DR', b.tldr_default ? '(technician to write)' : b.tldr)

    if (Array.isArray(b.h2_questions) && b.h2_questions.length) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'H2 Sections (as questions)', bold: true, size: 22 })], spacing: { before: 80, after: 60 } }))
      b.h2_questions.forEach(h => {
        children.push(new Paragraph({ children: [new TextRun({ text: '• ' + h, size: 22 })], indent: { left: 360 }, spacing: { after: 40 } }))
      })
    }

    if (Array.isArray(b.faqs) && b.faqs.length) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'FAQ Section', bold: true, size: 22 })], spacing: { before: 80, after: 60 } }))
      b.faqs.forEach(f => {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Q: ' + (f.q || ''), bold: true, size: 22 })], indent: { left: 360 }, spacing: { after: 20 } }))
        children.push(new Paragraph({ children: [new TextRun({ text: 'A: ' + (f.a || ''), size: 22 })], indent: { left: 360 }, spacing: { after: 60 } }))
      })
    }

    field('About the Company', b.company_blurb)
    field('Notes, Quotes & Statistics', b.notes_quotes_stats)
  })

  const doc = new Document({ sections: [{ children }] })
  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const safeName = (clientName || 'client').replace(/[^a-z0-9]/gi, '_')
  a.href = url
  a.download = `AEO_briefs_${safeName}.docx`
  a.click()
  URL.revokeObjectURL(url)
}

// ============================================================
// AEO Research Guidance step (Old Blogs - AEO step 3)
//   - Lists the 5 "Blog: [title]" child items from the project
//   - Each has a (+) to open the structured BriefEditor popup
//   - Batch approval lives on THIS step item:
//       awaiting_ad_approval -> approval_ad (Lauren) -> approval_client
//   - Once client-approved, a Bulk Assign button assigns all blog
//     children to one technician (due date defaults to June 15)
// ============================================================
function ResearchGuidanceEditor({ item, project, client, profiles, currentUser, onUpdated }) {
  const toast = useToast()
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingBlog, setEditingBlog] = useState(null)
  const [busy, setBusy] = useState(false)

  // bulk assign controls
  const [bulkAssignee, setBulkAssignee] = useState('')
  const [bulkDue, setBulkDue] = useState('2026-06-15')

  const loadBlogs = async () => {
    const { data, error } = await supabase
      .from('seo_project_items')
      .select('*')
      .eq('project_id', project.id)
      .like('topic', 'Blog: %')
      .order('sort_order')
    if (error) { toast('Error loading blogs: ' + error.message, 'error') }
    setBlogs(data || [])
    setLoading(false)
  }

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const { data } = await supabase
        .from('seo_project_items')
        .select('*')
        .eq('project_id', project.id)
        .like('topic', 'Blog: %')
        .order('sort_order')
      if (!cancelled) { setBlogs(data || []); setLoading(false) }
    }
    run()
    return () => { cancelled = true }
  }, [project.id])

  // Approval state (on this step item)
  const awaitingAd = item.awaiting_ad_approval && !item.approval_ad
  const adApproved = !!item.approval_ad
  const clientApproved = !!item.approval_client
  const canApprove = profiles.find(p => p.id === currentUser.id)?.role === 'supervising_ad'

  const briefsStarted = blogs.filter(b => b.brief && Object.keys(b.brief).length > 0).length
  const allBriefsStarted = blogs.length > 0 && briefsStarted === blogs.length

  const updateStep = async (updates, msg) => {
    setBusy(true)
    const { error } = await supabase.from('seo_project_items').update(updates).eq('id', item.id)
    setBusy(false)
    if (error) { toast('Error: ' + error.message, 'error'); return false }
    if (msg) toast(msg, 'success')
    onUpdated && onUpdated()
    return true
  }

  const sendToLauren = async () => {
    const ok = await updateStep({ awaiting_ad_approval: true }, 'Sent to Lauren for approval')
    if (!ok) return
    const { data: supers } = await supabase.from('profiles').select('id').eq('role', 'supervising_ad')
    const recipients = (supers || []).filter(s => s.id !== currentUser.id)
    if (recipients.length > 0) {
      await supabase.from('notifications').insert(recipients.map(r => ({
        user_id: r.id,
        type: 'comment',
        message: `AEO briefs for ${client?.name || 'a client'} are ready for your approval (${project.name})`,
      })))
    }
  }

  const laurenApproves = () => updateStep({ approval_ad: true, awaiting_ad_approval: false }, 'Approved — now send to the client')
  const sendToClient = () => {
    const clientEmail = client?.contact_email || ''
    const subject = encodeURIComponent(`Blog brief review — ${client?.name || ''}`)
    const intro = `Hi,\n\nWe've prepared briefs for ${blogs.length} blog update${blogs.length === 1 ? '' : 's'} and would love your approval before our team begins.\n\n`
    const briefsText = allBriefsToText(blogs, client?.name)
    const outro = `\n\nPlease reply to confirm these look good, or let us know any changes.\n\nBest,\nLMR Team`
    const body = encodeURIComponent(intro + briefsText + outro)
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(clientEmail)}&su=${subject}&body=${body}`, '_blank')
    toast('Opened client review email', 'success')
  }
  const downloadDoc = async () => {
    try {
      await downloadBriefsDocx(blogs, client?.name)
      toast('Brief doc downloaded', 'success')
    } catch (e) {
      toast('Download failed: ' + e.message, 'error')
    }
  }
  const clientApproves = () => updateStep({ approval_client: true, status: 'complete' }, 'Client approved — briefs locked')
  const reopen = () => updateStep({ approval_ad: false, approval_client: false, awaiting_ad_approval: false, status: 'in_progress' }, 'Reopened to draft')

  const bulkAssign = async () => {
    if (blogs.length === 0) { toast('No blogs to assign', 'error'); return }
    if (!bulkAssignee) { toast('Pick a technician first', 'error'); return }
    setBusy(true)
    const { error } = await supabase
      .from('seo_project_items')
      .update({ assigned_to: bulkAssignee, due_date: bulkDue || null })
      .in('id', blogs.map(b => b.id))
    setBusy(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(`Assigned ${blogs.length} blogs`, 'success')
    loadBlogs()
    onUpdated && onUpdated()
  }

  const stripPrefix = t => (t || '').replace(/^Blog:\s*/, '')

  return (
    <div>
      <div style={{
        background: 'var(--cream)', padding: '10px 14px', borderRadius: 8,
        marginBottom: 16, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5,
      }}>
        Prepare a brief for each of the {blogs.length || 5} blogs chosen above. When all briefs are ready,
        send the batch to Lauren, then to the client. After client approval, bulk-assign the blogs to a technician.
      </div>

      {/* Blog list */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
        Blog briefs ({briefsStarted}/{blogs.length} started)
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
      ) : blogs.length === 0 ? (
        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, background: 'var(--cream)', borderRadius: 8 }}>
          No blogs yet — fill in the "Select Blogs" step above first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {blogs.map((b, idx) => {
            const hasBrief = b.brief && Object.keys(b.brief).length > 0
            const assignee = profiles.find(p => p.id === b.assigned_to)
            return (
              <div key={b.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', background: 'var(--cream)', borderRadius: 8,
                border: `1px solid ${hasBrief ? '#c2dec8' : 'var(--border-light)'}`,
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 12, background: hasBrief ? '#2e6e3a' : 'var(--navy)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>{hasBrief ? '✓' : idx + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stripPrefix(b.topic) || '(untitled blog)'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {hasBrief ? 'Brief started' : 'No brief yet'}
                    {assignee && ` · ${assignee.full_name}`}
                    {b.due_date && ` · Due ${b.due_date}`}
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditingBlog(b)}
                  style={{ flexShrink: 0 }}
                >
                  {hasBrief ? 'Edit brief' : <><Plus size={12} /> Add brief</>}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Batch approval */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 22, marginBottom: 10 }}>
        Approval
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Lauren */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
          background: adApproved ? '#e8f5eb' : awaitingAd ? '#fff8e1' : 'var(--cream)',
          border: `1px solid ${adApproved ? '#c2dec8' : awaitingAd ? '#f0d98a' : 'var(--border-light)'}`,
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            background: adApproved ? '#2e6e3a' : '#fff', border: `1.5px solid ${adApproved ? '#2e6e3a' : 'var(--border)'}`,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
          }}>{adApproved ? '✓' : awaitingAd ? '⏳' : ''}</div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: adApproved ? '#2e6e3a' : awaitingAd ? '#8a6000' : 'var(--text-primary)' }}>
            {adApproved ? '✓ Lauren approved' : awaitingAd ? '⏳ Awaiting Lauren\u2019s approval' : 'Lauren\u2019s approval (internal)'}
          </div>
          {!adApproved && !awaitingAd && (
            <button className="btn btn-gold btn-sm" disabled={busy || !allBriefsStarted} onClick={sendToLauren} style={{ flexShrink: 0 }}>
              <Send size={12} /> Send to Lauren
            </button>
          )}
          {awaitingAd && canApprove && (
            <button className="btn btn-gold btn-sm" disabled={busy} onClick={laurenApproves} style={{ flexShrink: 0 }}>
              <CheckCircle size={12} /> Approve
            </button>
          )}
        </div>
        {!allBriefsStarted && !adApproved && !awaitingAd && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
            Start a brief for all {blogs.length} blogs before sending for approval.
          </div>
        )}

        {/* Deliver briefs to client — appears once Lauren has approved */}
        {adApproved && !clientApproved && (
          <div style={{ padding: 14, background: 'var(--cream)', borderRadius: 10, border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
              Send briefs to client for approval
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 10 }}>
              Put the full briefs in the email body, or download a Word doc to attach. Then mark approved once they confirm.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={sendToClient}>
                📨 Email briefs in body
              </button>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={downloadDoc}>
                <Download size={12} /> Download .docx
              </button>
            </div>
          </div>
        )}

        {/* Client */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
          opacity: adApproved ? 1 : 0.5,
          background: clientApproved ? '#e8f5eb' : 'var(--cream)',
          border: `1px solid ${clientApproved ? '#c2dec8' : 'var(--border-light)'}`,
        }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            background: clientApproved ? '#2e6e3a' : '#fff', border: `1.5px solid ${clientApproved ? '#2e6e3a' : 'var(--border)'}`,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
          }}>{clientApproved ? '✓' : ''}</div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: clientApproved ? '#2e6e3a' : 'var(--text-primary)' }}>
            {clientApproved ? '✓ Client approved' : 'Client approval'}
          </div>
          {adApproved && !clientApproved && (
            <button className="btn btn-gold btn-sm" disabled={busy} onClick={clientApproves} style={{ flexShrink: 0 }}>
              <CheckCircle size={12} /> Mark approved
            </button>
          )}
        </div>
      </div>

      {(adApproved || clientApproved) && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={reopen}>
          ↺ Reopen / unlock
        </button>
      )}

      {/* Bulk assign — appears once client-approved */}
      {clientApproved && blogs.length > 0 && (
        <div style={{ marginTop: 22, padding: 14, background: 'var(--cream)', borderRadius: 10, border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
            Bulk assign blogs to a technician
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Technician</label>
              <select className="form-control" value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}>
                <option value="">Pick a person…</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">Due date</label>
              <input className="form-control" type="date" value={bulkDue} onChange={e => setBulkDue(e.target.value)} />
            </div>
            <button className="btn btn-gold" disabled={busy} onClick={bulkAssign} style={{ flexShrink: 0 }}>
              Assign all {blogs.length}
            </button>
          </div>
        </div>
      )}

      {editingBlog && (
        <BriefEditor
          blog={editingBlog}
          locked={clientApproved}
          onClose={() => setEditingBlog(null)}
          onSaved={() => { setEditingBlog(null); loadBlogs(); onUpdated && onUpdated() }}
        />
      )}
    </div>
  )
}

// ============================================================
// BriefEditor — structured brief popup for a single blog
// ============================================================
function BriefEditor({ blog, locked, onClose, onSaved }) {
  const toast = useToast()
  const b = (blog.brief && typeof blog.brief === 'object') ? blog.brief : {}

  const [h1Question, setH1Question]   = useState(b.h1_question || '')
  const [dateUpdated, setDateUpdated] = useState(b.date_updated || '')
  const [author, setAuthor]           = useState(b.author || '')
  const [authorBio, setAuthorBio]     = useState(b.author_bio || '')
  const [tldr, setTldr]               = useState(b.tldr || '')
  const [tldrDefault, setTldrDefault] = useState(b.tldr_default ?? true)
  const [h2s, setH2s]                 = useState(Array.isArray(b.h2_questions) && b.h2_questions.length ? b.h2_questions : [''])
  const [faqs, setFaqs]               = useState(Array.isArray(b.faqs) && b.faqs.length ? b.faqs : [{ q: '', a: '' }])
  const [companyBlurb, setCompanyBlurb] = useState(b.company_blurb || '')
  const [notesQuotesStats, setNotesQuotesStats] = useState(b.notes_quotes_stats || '')
  const [saving, setSaving] = useState(false)

  const stripPrefix = t => (t || '').replace(/^Blog:\s*/, '')

  const persist = async () => {
    const brief = {
      h1_question: h1Question.trim(),
      date_updated: dateUpdated || '',
      author: author.trim(),
      author_bio: authorBio.trim(),
      tldr: tldr.trim(),
      tldr_default: !!tldrDefault,
      h2_questions: h2s.map(s => s.trim()).filter(Boolean),
      faqs: faqs.map(f => ({ q: (f.q || '').trim(), a: (f.a || '').trim() })).filter(f => f.q || f.a),
      company_blurb: companyBlurb.trim(),
      notes_quotes_stats: notesQuotesStats.trim(),
    }

    // Also write a readable version into the blog's notes so the technician
    // sees the brief directly on the blog task (not just in the brief popup).
    const noteLines = ['── BRIEF ──']
    if (brief.h1_question)  noteLines.push(`H1: ${brief.h1_question}`)
    if (brief.date_updated) noteLines.push(`Last Updated (in post): ${brief.date_updated}`)
    if (brief.author)       noteLines.push(`Author: ${brief.author}`)
    if (brief.author_bio)   noteLines.push(`Author bio: ${brief.author_bio}`)
    if (brief.tldr_default) noteLines.push('TL;DR: (technician to write)')
    else if (brief.tldr)    noteLines.push(`TL;DR: ${brief.tldr}`)
    if (brief.h2_questions.length) {
      noteLines.push('H2 sections:')
      brief.h2_questions.forEach(h => noteLines.push(`  - ${h}`))
    }
    if (brief.faqs.length) {
      noteLines.push('FAQs:')
      brief.faqs.forEach(f => { noteLines.push(`  Q: ${f.q}`); noteLines.push(`  A: ${f.a}`) })
    }
    if (brief.company_blurb)      noteLines.push(`About the company: ${brief.company_blurb}`)
    if (brief.notes_quotes_stats) noteLines.push(`Notes/Quotes/Stats: ${brief.notes_quotes_stats}`)
    const briefNotes = noteLines.join('\n')

    return supabase.from('seo_project_items').update({ brief, notes: briefNotes }).eq('id', blog.id)
  }

  // Save and close (notifies parent → list refresh + closes the popup)
  const save = async () => {
    setSaving(true)
    const { error } = await persist()
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Brief saved', 'success')
    onSaved && onSaved()
  }

  // Save draft and KEEP editing (so you can step away and come back)
  const saveDraft = async () => {
    setSaving(true)
    const { error } = await persist()
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Draft saved — keep going', 'success')
  }

  const fieldDisabled = locked

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 95, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,43,74,0.35)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 760, background: '#fff', borderRadius: 12, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 80px)' }}>
        {/* Header */}
        <div style={{ background: 'var(--navy)', padding: '16px 22px', color: '#fff', borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AEO Blog Brief</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stripPrefix(blog.topic) || '(untitled blog)'}
            </div>
          </div>
          <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', flexShrink: 0 }} onClick={onClose}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', flex: 1, overflowY: 'auto' }}>
          {locked && (
            <div style={{ background: '#e8f5eb', color: '#2e6e3a', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 16, fontWeight: 600 }}>
              ✓ This brief is client-approved and locked. Reopen the step to edit.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">H1 (as a question)</label>
            <input className="form-control" value={h1Question} disabled={fieldDisabled} onChange={e => setH1Question(e.target.value)} placeholder="e.g. How Do I Know If I'm Burnt Out or Just Stressed?" />
          </div>

          <div className="form-group">
            <label className="form-label">"Last Updated" date (publishes on the blog)</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
              This is the date the technician puts <strong>in the blog post itself</strong> (the visible "Last Updated: ___" line readers see) — not a task due date.
            </div>
            <input className="form-control" type="date" value={dateUpdated} disabled={fieldDisabled} onChange={e => setDateUpdated(e.target.value)} style={{ maxWidth: 220 }} />
          </div>

          <div className="form-group">
            <label className="form-label">Author</label>
            <input className="form-control" value={author} disabled={fieldDisabled} onChange={e => setAuthor(e.target.value)} placeholder="Author name" />
          </div>

          <div className="form-group">
            <label className="form-label">Author Bio (short, blog-relevant)</label>
            <textarea className="form-control" rows={2} value={authorBio} disabled={fieldDisabled} onChange={e => setAuthorBio(e.target.value)} placeholder="2-3 sentences positioning this author as relevant to the topic…" />
          </div>

          {/* TL;DR */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>TL;DR</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>
                <input type="checkbox" checked={tldrDefault} disabled={fieldDisabled} onChange={e => setTldrDefault(e.target.checked)} />
                Let the technician write it
              </label>
            </label>
            {!tldrDefault && (
              <textarea className="form-control" rows={3} value={tldr} disabled={fieldDisabled} onChange={e => setTldr(e.target.value)} placeholder="Write the TL;DR summary, or check the box above to have the technician draft it." />
            )}
            {tldrDefault && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
                Technician will write a 2-3 sentence TL;DR summarizing the answer up top.
              </div>
            )}
          </div>

          {/* H2 questions */}
          <div className="form-group">
            <label className="form-label">H2 Sections (as questions)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {h2s.map((h, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input className="form-control" value={h} disabled={fieldDisabled} onChange={e => setH2s(arr => arr.map((x, i) => i === idx ? e.target.value : x))} placeholder={`H2 #${idx + 1} — phrased as a question`} style={{ flex: 1 }} />
                  {!fieldDisabled && (
                    <button className="btn-icon" title="Remove" onClick={() => setH2s(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr)}><X size={12} /></button>
                  )}
                </div>
              ))}
            </div>
            {!fieldDisabled && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => setH2s(arr => [...arr, ''])}><Plus size={12} /> Add H2</button>
            )}
          </div>

          {/* FAQ pairs */}
          <div className="form-group">
            <label className="form-label">FAQ Section (accordion — 2-4 sentence answers)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {faqs.map((f, idx) => (
                <div key={idx} style={{ border: '1px solid var(--border-light)', borderRadius: 8, padding: 10, background: 'var(--cream)' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <input className="form-control" value={f.q} disabled={fieldDisabled} onChange={e => setFaqs(arr => arr.map((x, i) => i === idx ? { ...x, q: e.target.value } : x))} placeholder={`FAQ question #${idx + 1}`} style={{ flex: 1 }} />
                    {!fieldDisabled && (
                      <button className="btn-icon" title="Remove" onClick={() => setFaqs(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr)}><X size={12} /></button>
                    )}
                  </div>
                  <textarea className="form-control" rows={2} value={f.a} disabled={fieldDisabled} onChange={e => setFaqs(arr => arr.map((x, i) => i === idx ? { ...x, a: e.target.value } : x))} placeholder="Answer (2-4 sentences)" />
                </div>
              ))}
            </div>
            {!fieldDisabled && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => setFaqs(arr => [...arr, { q: '', a: '' }])}><Plus size={12} /> Add FAQ</button>
            )}
          </div>

          {/* Company blurb */}
          <div className="form-group">
            <label className="form-label">About the Company (detailed, blog-relevant — for the bottom)</label>
            <textarea className="form-control" rows={4} value={companyBlurb} disabled={fieldDisabled} onChange={e => setCompanyBlurb(e.target.value)} placeholder="A fuller company blurb tailored to this blog's topic, to close the post…" />
          </div>

          {/* Notes / Quotes / Statistics */}
          <div className="form-group">
            <label className="form-label">Notes, Quotes & Statistics</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
              Direct client/clinician quotes (with name + credentials), supporting stats, and any other guidance for the technician to weave in. Attributed expert quotes are gold for AEO — they can't be scraped from anywhere else.
            </div>
            <textarea className="form-control" rows={5} value={notesQuotesStats} disabled={fieldDisabled} onChange={e => setNotesQuotesStats(e.target.value)} placeholder={'e.g.\n"Most clients think burnout means they need a vacation…" — Dr. Sarah Chen, LCPC\nStat: 76% of professionals report burnout (Deloitte, 2023)\nNote: link internally to the anxiety service page'} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: 16, borderTop: '1px solid var(--border-light)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {!locked && (
            <>
              <button className="btn btn-ghost" onClick={saveDraft} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</button>
              <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving…' : '💾 Save & close'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}